import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { formatDate } from '../format';
import {
  accessReason,
  checkLabel,
  codeLabel,
  groupByField,
  pageRefusalErrorText,
  pageRefusalPath,
  refusedPageParts,
  writerLabel,
  type PageRefusalDetail,
  type PageRefusalProblem,
} from '../pageRefusals';

// One refused board page save (TOM-672, ADR 0395): who saved it, every reason
// the page check gave, the problems that were already on the page before the
// edit, and the page's HTML and CSS. The page is the owner's untrusted
// content, so it is shown as text in <pre> blocks and never drawn. The list
// hands its access reason over; opened from a link, the page asks for one.

function Problems({ problems }: { problems: readonly PageRefusalProblem[] }) {
  return (
    <div className="scroll-x">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Part</th>
            <th>Problem</th>
          </tr>
        </thead>
        <tbody>
          {groupByField(problems).map((g) =>
            g.messages.map((m, i) => (
              <tr key={`${g.field}-${i}`}>
                <td>{i === 0 ? <code>{g.field}</code> : null}</td>
                <td>{m}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function AdminNeoPageRefusalDetail() {
  const { id = '' } = useParams();
  const location = useLocation();
  const passed = (location.state as { reason?: unknown } | null)?.reason;
  const [reason, setReason] = useState(typeof passed === 'string' ? passed : '');
  const [data, setData] = useState<PageRefusalDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(
    typeof passed === 'string' && accessReason(passed) !== null
  );

  const load = useCallback(
    async (withReason: string) => {
      const path = pageRefusalPath(id, withReason);
      if (path === null) return;
      setLoading(true);
      setError(null);
      try {
        setData(await adminApi<PageRefusalDetail>(path));
      } catch (err) {
        const e = err as { status?: number; body?: { error?: unknown } };
        setError(
          pageRefusalErrorText(
            e.status,
            typeof e.body?.error === 'string' ? e.body.error : undefined
          )
        );
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  // Opened from the list: its reason reads the save straight away.
  useEffect(() => {
    if (typeof passed === 'string' && accessReason(passed) !== null) void load(passed);
  }, [passed, load]);

  function open(e: FormEvent) {
    e.preventDefault();
    void load(reason);
  }

  const back = (
    <p>
      <Link to="/admin/neo/page-refusals">← Page refusals</Link>
    </p>
  );

  if (data === null) {
    return (
      <section className="admin-section">
        {back}
        <header className="admin-section-header">
          <h1>Refused page</h1>
        </header>
        {loading ? <p className="admin-loading-row">Loading…</p> : null}
        {!loading ? (
          <>
            <div className="admin-auth-banner">
              A refused page is the owner&apos;s content. Your access is logged with your account
              and your reason.
            </div>
            <form className="neo-admin-reason" onSubmit={open}>
              <label htmlFor="page-refusal-detail-reason">Reason for viewing</label>
              <input
                id="page-refusal-detail-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. finding out why this page was turned down"
                minLength={3}
                maxLength={200}
                required
              />
              <button
                type="submit"
                className="admin-button"
                disabled={accessReason(reason) === null}
              >
                Open refused page
              </button>
            </form>
          </>
        ) : null}
        {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
      </section>
    );
  }

  const parts = refusedPageParts(data.page);
  return (
    <section className="admin-section">
      {back}
      <header className="admin-section-header">
        <h1>Refused page</h1>
      </header>
      <div className="admin-auth-banner">Viewing an owner&apos;s refused page — access logged.</div>

      <dl className="admin-defs">
        <dt>When</dt>
        <dd>{formatDate(data.createdAt)}</dd>
        <dt>Boat</dt>
        <dd>
          {data.boatName ?? '—'} <code>{data.boatId}</code>
        </dd>
        <dt>Owner</dt>
        <dd>
          {data.userId !== null ? (
            <Link to={`/admin/users/${data.userId}`}>{data.userEmail ?? data.userId}</Link>
          ) : (
            '—'
          )}
        </dd>
        <dt>Saved by</dt>
        <dd>{writerLabel(data.writer)}</dd>
        <dt>Board</dt>
        <dd>{data.boardSlug !== null ? <code>{data.boardSlug}</code> : 'Chat answer'}</dd>
        <dt>Item</dt>
        <dd>{data.itemId !== null ? <code>{data.itemId}</code> : 'A new page'}</dd>
        <dt>Check</dt>
        <dd>
          {checkLabel(data.check)} · {codeLabel(data.code)}
        </dd>
        {data.turnRef !== null ? (
          <>
            <dt>Neo turn</dt>
            <dd>
              <code>{data.turnRef}</code>
            </dd>
          </>
        ) : null}
      </dl>

      <h2>Why it was refused ({data.refusals.length})</h2>
      {data.refusals.length === 0 ? (
        <p className="admin-empty">No reasons were recorded.</p>
      ) : (
        <Problems problems={data.refusals} />
      )}

      {data.kept.length > 0 ? (
        <>
          <h2>Already on the page before the edit, so not refused ({data.kept.length})</h2>
          <Problems problems={data.kept} />
        </>
      ) : null}

      <h2>The page</h2>
      {parts.truncatedBytes !== null ? (
        <p className="admin-muted">
          The page was too big to keep ({parts.truncatedBytes.toLocaleString()} bytes).
        </p>
      ) : null}
      {parts.truncatedBytes === null && parts.html === null && parts.css === null ? (
        <p className="admin-muted">
          The save sent no HTML or CSS of its own. What it sent is below.
        </p>
      ) : null}
      {parts.html !== null ? (
        <>
          <h3>HTML</h3>
          <div className="admin-info-card">
            <pre className="eval-pre">{parts.html}</pre>
          </div>
        </>
      ) : null}
      {parts.css !== null ? (
        <>
          <h3>CSS</h3>
          <div className="admin-info-card">
            <pre className="eval-pre">{parts.css.length > 0 ? parts.css : '(none)'}</pre>
          </div>
        </>
      ) : null}
      {parts.rest !== null ? (
        <details className="eval-drawer-details">
          <summary>Everything else {data.check === 'static' ? 'sent' : 'drawn'}</summary>
          <div className="admin-info-card">
            <pre className="eval-pre">{parts.rest}</pre>
          </div>
        </details>
      ) : null}
    </section>
  );
}
